import auth_helper from '../helpers/auth_helper';
import FinancingHelper from '../helpers/financing_helper';

const PollingDispatcher = {
  setLastPollStartTime() {
    this.setStoreVal('lastPollStartTime', new Date().getTime());
  },

  beginPolling({ action, interval, test, wait, reduxData, isUnderwritingScreen }) {
    const calcCustomerHash = () => {
      if (isUnderwritingScreen) {
        return this.getStoreVal('view')?.decisionId;
      }
      return `${this.getStoreVal('proposal')?.id}-${this.getStoreVal('customer')?.id}-${
        this.getStoreVal('financingMethod')?.id
      }-${this.getStoreVal('creditCheck')?.id}`; //-${this.getStoreVal('financingRequest')?.id}
    };

    const webdriver =
      navigator.webdriver ||
      window.document.documentElement.getAttribute('webdriver') ||
      '_Selenium_IDE_Recorder' in window ||
      '__webdriver_script_fn' in document;

    const pollIntervalIds = this.getStoreVal('pollIntervalIds') || {};

    if (pollIntervalIds[action.type]) {
      console.warn(`Already polling ${action.type}`);
      return;
    }

    let docHidden = false;
    document.addEventListener('visibilitychange', function () {
      docHidden = document.hidden;
    });

    const customerHash = calcCustomerHash();

    this.setStoreVal('lastPollStartTime', new Date().getTime());

    const dispatcher = () => {
      // if (
      //   this.getStoreVal('lastPollStartTime') &&
      //   new Date().getTime() - this.getStoreVal('lastPollStartTime') > 2 * 60 * 1000 * 60
      // ) {
      //   return; // stop polling after 2 hours
      // }
      if (customerHash !== calcCustomerHash()) {
        return;
      }
      // if no session dont poll
      if (!isUnderwritingScreen && !auth_helper.getSessionToken() && !auth_helper.isUWImpersonating()) {
        return;
      }

      if (
        (!test || test()) &&
        (webdriver || !document.hidden || !docHidden) &&
        !sessionStorage.getItem('pausePollingFlag')
      ) {
        let data = action.data;

        if (typeof reduxData === 'function') {
          data = reduxData();
          if (!data) {
            return; // Abort!
          }
        }

        this.dispatch({ type: action.type, data: data });
      }
      docHidden = document.hidden;
    };

    if (!wait) {
      dispatcher(); // interval uses a wait/fire/wait/fire model, we want fire/wait/fire/wait
    }

    let timeout = interval;
    if (window.sessionStorage.getItem('enablePolly') === 'replay') {
      timeout = window.sessionStorage.getItem('beamPollingSpeed')
        ? parseInt(window.sessionStorage.getItem('beamPollingSpeed'))
        : 150;
    } else if (window.sessionStorage.getItem('enablePolly') !== 'record' && !!webdriver) {
      timeout = interval / 5;
    }

    const intervalId = setInterval(dispatcher, timeout);

    this.mergeStoreVal('pollIntervalIds', { [action.type]: intervalId });
  },

  beginPollEligibility({ data, interval }) {
    let customer = this.getStoreVal('customer');
    if (customer && customer.status === 'Cancelled') {
      console.log('Ignoring polling on a cancelled customer');
      return;
    }
    const pollIntervalIds = this.getStoreVal('pollIntervalIds') || {};
    if (pollIntervalIds['fetchEligibility']) {
      this.dispatch({ type: 'endPolling', data: 'fetchEligibility' });
    }
    this.dispatch({
      type: 'beginPolling',
      data: {
        action: { type: 'fetchEligibility', data },
        interval,
        test: () => {
          const eligibility = this.getStoreVal('eligibility');

          return eligibility && eligibility.creditCheck && eligibility.creditCheck.id;
        },
      },
    });
  },

  beginPollPrescreen({ data, cb }) {
    let hasHitCB = false;
    this.dispatch({
      type: 'beginPolling',
      data: {
        action: { type: 'fetchPrescreen', data },
        interval: 5000,
        test: () => {
          const isBad =
            this.getStoreVal('proposal') &&
            (!this.getStoreVal('prescreenProducts') || this.getStoreVal('prescreenProducts').length === 0);

          if (!isBad && typeof cb === 'function' && !hasHitCB) {
            hasHitCB = true;
            cb();
          }

          return isBad;
        },
      },
    });
  },

  beginPollEligibilitySlow(data) {
    this.dispatch({ type: 'beginPollEligibility', data: { data, interval: 30000 } });
  },

  beginPollEligibilityFast(data) {
    this.dispatch({ type: 'beginPollEligibility', data: { data, interval: 5000 } });
  },

  beginPollSignatures(data) {
    this.dispatch({
      type: 'beginPolling',
      data: {
        action: { type: 'fetchSignature', data },
        interval: 30000,
        test: () => {
          const documents = this.getStoreVal('legalDocuments');
          return documents && documents.length;
        },
      },
    });
  },

  beginPollFinancingMethod() {
    // 1800000 = 30 minutes
    this.dispatch({
      type: 'beginPolling',
      data: { action: { type: 'refreshFinancingMethods' }, interval: 1800000, wait: true },
    });
  },

  beginPollFinancingRequest() {
    this.dispatch({
      type: 'beginPolling',
      data: {
        action: { type: 'fetchFinancingRequest' },
        interval: 30000,
        test: () => {
          const proposal = this.getStoreVal('proposal');
          return proposal && proposal.customerId && proposal.financingRequestId;
        },
        reduxData: () => {
          const proposal = this.getStoreVal('proposal');
          return { customerId: proposal?.customerId };
        },
      },
    });
  },

  beginPollCreditTasks() {
    this.dispatch({
      type: 'beginPolling',
      data: {
        action: { type: 'getCreditTasks' },
        interval: 60000,
        test: () => {
          const eligibility = this.getStoreVal('eligibility');
          const homeownerData = this.getStoreVal('homeownerData');
          return (
            (eligibility &&
              eligibility.creditCheck &&
              eligibility.creditCheck.hasStips &&
              eligibility.creditCheck.financingProducts &&
              eligibility.creditCheck.financingProducts[0].externalId) ||
            (homeownerData && homeownerData.stips?.some(({ externalId }) => externalId))
          );
        },
      },
    });
  },

  beginStatusNotesPolling() {
    this.dispatch({
      type: 'beginPolling',
      data: {
        action: { type: 'fetchStipStatusNotes', data: { customerId: this.getStoreVal('customer').id } },
        interval: 30000,
        test: () =>
          this.getStoreVal('eligibility')?.creditCheck?.hasStips &&
          !FinancingHelper.isComplete(this.getStoreVal('eligibility')?.creditCheck?.financingProducts[0]?.rateCards),
      },
    });
  },

  beginProposalPagePolling({ customerId, proposalId }) {
    this.dispatch({ type: 'beginPollFinancingRequest' });
    this.dispatch({ type: 'beginPollEligibilitySlow', data: { customerId } });
    this.dispatch({ type: 'beginPollFinancingMethod' });
    this.dispatch({ type: 'beginPollSignatures', data: proposalId });
  },

  beginPollDecision(data) {
    this.dispatch({
      type: 'beginPolling',
      data: {
        action: { type: 'fetchDecisionPage' },
        isUnderwritingScreen: true,
        interval: 15000,
        test: () => {
          return (
            this.getStoreVal('view')?.page === 'decision' &&
            this.getStoreVal('decisioningPageData')?.status === 'submitted'
          );
        },
        reduxData: () => {
          return { id: this.getStoreVal('view')?.decisionId };
        },
      },
    });
  },

  endPolling(type) {
    const pollIntervalIds = this.getStoreVal('pollIntervalIds') || {};
    clearInterval(pollIntervalIds[type]);
    this.mergeStoreVal('pollIntervalIds', { [type]: undefined });
  },

  isPollingActive(type) {
    const pollIntervalIds = this.getStoreVal('pollIntervalIds') || {};
    const pausePolling = sessionStorage.getItem('pausePollingFlag');
    return !!pollIntervalIds[type] && !pausePolling;
  },

  clearPolling() {
    const pollIntervalIds = this.getStoreVal('pollIntervalIds') || {};
    Object.keys(pollIntervalIds).forEach((type) => {
      clearInterval(pollIntervalIds[type]);
      this.mergeStoreVal('pollIntervalIds', { [type]: undefined });
    });
  },

  pausePolling() {
    sessionStorage.setItem('pausePollingFlag', true);
  },

  resumePolling() {
    sessionStorage.removeItem('pausePollingFlag');
  },

  isPollingPaused() {
    return sessionStorage.getItem('pausePollingFlag');
  },
};
export default PollingDispatcher;
